import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { PageHeader, GlassCard, KpiCard } from "@/components/Primitives";
import {
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, Tooltip, CartesianGrid, XAxis, YAxis, Legend,
} from "recharts";
import { Send, Trophy, X as XIcon, Target, Wallet, Calendar } from "lucide-react";

const COLORS = ["#007AFF", "#34C759", "#FF9F0A", "#BF5AF2", "#5AC8FA", "#FF6482", "#FFD60A"];
const tooltipStyle = {
  contentStyle: {
    background: "rgba(15,15,18,0.92)", border: "1px solid rgba(255,255,255,0.10)",
    borderRadius: 14, backdropFilter: "blur(20px)", color: "#fff", fontSize: 12,
  },
};

export default function Analytics() {
  const [a, setA] = useState(null);
  useEffect(() => {
    (async () => { const { data } = await api.get("/analytics"); setA(data); })();
  }, []);

  if (!a) return <div className="text-white/40">Loading…</div>;

  return (
    <div data-testid="analytics-page">
      <PageHeader title="Analytics" subtitle="Funnel performance, sources, and lost-deal patterns." testId="analytics-header" />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
        <KpiCard testId="a-leads" label="Leads This Month" value={a.leads_this_month} icon={Send} accent="from-sky-400 to-sky-600" />
        <KpiCard testId="a-discovery" label="Discovery Calls" value={a.discovery_calls} icon={Calendar} accent="from-indigo-400 to-indigo-600" />
        <KpiCard testId="a-won" label="Projects Won" value={a.projects_won} icon={Trophy} accent="from-emerald-400 to-emerald-600" />
        <KpiCard testId="a-lost" label="Projects Lost" value={a.projects_lost} icon={XIcon} accent="from-rose-400 to-rose-600" />
        <KpiCard testId="a-close" label="Close Rate" value={`${a.close_rate}%`} icon={Target} accent="from-teal-400 to-teal-600" />
        <KpiCard testId="a-month" label="Revenue This Month" value={fmtMoney(a.revenue_this_month)} icon={Wallet} accent="from-[#007AFF] to-[#0050C8]" />
        <KpiCard testId="a-year" label="Revenue This Year" value={fmtMoney(a.revenue_this_year)} icon={Wallet} accent="from-violet-400 to-violet-600" />
        <KpiCard testId="a-proposals" label="Proposals Sent" value={a.proposals_sent} icon={Send} accent="from-amber-400 to-amber-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <GlassCard testId="chart-sources">
          <div className="font-display text-xl mb-4">Lead Sources</div>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={a.lead_sources} dataKey="count" nameKey="source" innerRadius={60} outerRadius={100} stroke="none">
                {a.lead_sources.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip {...tooltipStyle} cursor={{ fill: "transparent" }} />
              <Legend wrapperStyle={{ color: "rgba(255,255,255,0.5)", fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </GlassCard>

        <GlassCard testId="chart-lost">
          <div className="font-display text-xl mb-4">Lost Reasons</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={a.lost_reasons.length ? a.lost_reasons : [{ reason: "None", count: 0 }]}>
              <CartesianGrid stroke="rgba(255,255,255,0.04)" vertical={false} />
              <XAxis dataKey="reason" stroke="rgba(255,255,255,0.4)" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="rgba(255,255,255,0.4)" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip {...tooltipStyle} cursor={{ fill: "rgba(255,100,130,0.08)" }} />
              <Bar dataKey="count" fill="#FF6482" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </GlassCard>
      </div>
    </div>
  );
}
